import React, { useId, useMemo } from 'react';

import {
	getFillColor,
	getHexagonPoints,
	getNodeOverlapLayout,
	getTrianglePoints,
} from './spiderweb-utils';

import type { FillColor, OverlapFill, WebNodeConfig } from './spiderweb-types';
import type { NodeCenterPct, NodeOverlapLayout } from './spiderweb-utils';

type WorldAabb = { minX: number; maxX: number; minY: number; maxY: number };

function localToWorld(lx: number, ly: number, L: NodeOverlapLayout): [number, number] {
	const rad = (L.rotate * Math.PI) / 180;
	const c = Math.cos(rad);
	const s = Math.sin(rad);
	return [L.cx + lx * c - ly * s, L.cy + lx * s + ly * c];
}

function worldToLocal(wx: number, wy: number, L: NodeOverlapLayout): [number, number] {
	const dx = wx - L.cx;
	const dy = wy - L.cy;
	const rad = (-L.rotate * Math.PI) / 180;
	const c = Math.cos(rad);
	const s = Math.sin(rad);
	return [dx * c - dy * s, dx * s + dy * c];
}

function layoutWorldAabb(L: NodeOverlapLayout): WorldAabb {
	const { r, size, pillWidth, rectangleWidth, rectangleHeight, shape } = L;
	const corners: [number, number][] = (() => {
		switch (shape) {
			case 'circle':
				return [
					[-r, -r],
					[r, -r],
					[r, r],
					[-r, r],
				];
			case 'pill': {
				const x0 = -pillWidth / 2;
				const x1 = pillWidth / 2;
				const y0 = -r;
				const y1 = -r + size;
				return [
					[x0, y0],
					[x1, y0],
					[x1, y1],
					[x0, y1],
				];
			}
			case 'rectangle': {
				const rh = rectangleHeight;
				const x0 = -rectangleWidth / 2;
				const x1 = rectangleWidth / 2;
				const y0 = -rh / 2;
				const y1 = rh / 2;
				return [
					[x0, y0],
					[x1, y0],
					[x1, y1],
					[x0, y1],
				];
			}
			case 'hexagon': {
				const pts: [number, number][] = [];
				for (let i = 0; i < 6; i++) {
					const a = (Math.PI / 3) * i - Math.PI / 2;
					pts.push([r * Math.cos(a), r * Math.sin(a)]);
				}
				return pts;
			}
			case 'triangle':
				return [
					[0, -r],
					[-r * 0.866, r * 0.5],
					[r * 0.866, r * 0.5],
				];
			default:
				return [
					[-r, -r],
					[r, -r],
					[r, r],
					[-r, r],
				];
		}
	})();

	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;
	for (const [lx, ly] of corners) {
		const [wx, wy] = localToWorld(lx, ly, L);
		minX = Math.min(minX, wx);
		maxX = Math.max(maxX, wx);
		minY = Math.min(minY, wy);
		maxY = Math.max(maxY, wy);
	}
	return { minX, maxX, minY, maxY };
}

function intersectWorldAabbs(a: WorldAabb, b: WorldAabb): WorldAabb | null {
	const minX = Math.max(a.minX, b.minX);
	const maxX = Math.min(a.maxX, b.maxX);
	const minY = Math.max(a.minY, b.minY);
	const maxY = Math.min(a.maxY, b.maxY);
	if (minX >= maxX || minY >= maxY) return null;
	return { minX, maxX, minY, maxY };
}

function pointInLocalHexagon(lx: number, ly: number, r: number): boolean {
	const vx: number[] = [];
	const vy: number[] = [];
	for (let i = 0; i < 6; i++) {
		const a = (Math.PI / 3) * i - Math.PI / 2;
		vx.push(r * Math.cos(a));
		vy.push(r * Math.sin(a));
	}
	let inside = false;
	for (let i = 0; i < 6; i++) {
		const j = (i + 5) % 6;
		const xi = vx[i] as number;
		const yi = vy[i] as number;
		const xj = vx[j] as number;
		const yj = vy[j] as number;
		if (yi > ly !== yj > ly && lx < ((xj - xi) * (ly - yi)) / (yj - yi + 1e-12) + xi) {
			inside = !inside;
		}
	}
	return inside;
}

function pointInLocalTriangle(lx: number, ly: number, r: number): boolean {
	const x1 = 0;
	const y1 = -r;
	const x2 = -r * 0.866;
	const y2 = r * 0.5;
	const x3 = r * 0.866;
	const y3 = r * 0.5;
	const sign = (p1x: number, p1y: number, p2x: number, p2y: number, p3x: number, p3y: number) =>
		(p1x - p3x) * (p2y - p3y) - (p2x - p3x) * (p1y - p3y);
	const d1 = sign(lx, ly, x1, y1, x2, y2);
	const d2 = sign(lx, ly, x2, y2, x3, y3);
	const d3 = sign(lx, ly, x3, y3, x1, y1);
	const hasNeg = d1 < -1e-6 || d2 < -1e-6 || d3 < -1e-6;
	const hasPos = d1 > 1e-6 || d2 > 1e-6 || d3 > 1e-6;
	return !(hasNeg && hasPos);
}

function pointInLocalShape(lx: number, ly: number, L: NodeOverlapLayout): boolean {
	const { r, size, pillWidth, rectangleWidth, rectangleHeight, shape } = L;
	switch (shape) {
		case 'circle':
			return lx * lx + ly * ly <= r * r + 1e-6;
		case 'pill':
			return (
				lx >= -pillWidth / 2 - 1e-6 &&
				lx <= pillWidth / 2 + 1e-6 &&
				ly >= -r - 1e-6 &&
				ly <= -r + size + 1e-6
			);
		case 'rectangle': {
			const rh = rectangleHeight;
			return (
				lx >= -rectangleWidth / 2 - 1e-6 &&
				lx <= rectangleWidth / 2 + 1e-6 &&
				ly >= -rh / 2 - 1e-6 &&
				ly <= rh / 2 + 1e-6
			);
		}
		case 'hexagon':
			return pointInLocalHexagon(lx, ly, r);
		case 'triangle':
			return pointInLocalTriangle(lx, ly, r);
		default:
			return lx * lx + ly * ly <= r * r + 1e-6;
	}
}

function pointInWorldShape(wx: number, wy: number, L: NodeOverlapLayout): boolean {
	const [lx, ly] = worldToLocal(wx, wy, L);
	return pointInLocalShape(lx, ly, L);
}

const INTERSECTION_AABB_GRID = 56;

function getShapeIntersectionAabb(Ls: NodeOverlapLayout, Lt: NodeOverlapLayout): WorldAabb | null {
	const ab = intersectWorldAabbs(layoutWorldAabb(Ls), layoutWorldAabb(Lt));
	if (!ab) return null;

	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	let found = false;
	const gw = INTERSECTION_AABB_GRID;
	const w = ab.maxX - ab.minX;
	const h = ab.maxY - ab.minY;

	for (let i = 0; i <= gw; i++) {
		for (let j = 0; j <= gw; j++) {
			const x = ab.minX + (i / gw) * w;
			const y = ab.minY + (j / gw) * h;
			if (pointInWorldShape(x, y, Ls) && pointInWorldShape(x, y, Lt)) {
				found = true;
				minX = Math.min(minX, x);
				maxX = Math.max(maxX, x);
				minY = Math.min(minY, y);
				maxY = Math.max(maxY, y);
			}
		}
	}

	if (!found) return null;

	const pad = Math.max((maxX - minX) * 0.02, (maxY - minY) * 0.02, 0.5);
	return {
		minX: minX - pad,
		maxX: maxX + pad,
		minY: minY - pad,
		maxY: maxY + pad,
	};
}

function clipPathShape(L: NodeOverlapLayout): React.ReactNode {
	const {
		cx,
		cy,
		r,
		size,
		pillWidth,
		rectangleWidth,
		rectangleHeight,
		rectangleBorderRadius,
		rotate,
		shape,
	} = L;
	const tf = `translate(${cx},${cy}) rotate(${rotate})`;
	switch (shape) {
		case 'circle':
			return <circle cx={cx} cy={cy} r={r} />;
		case 'pill':
			return (
				<rect x={-pillWidth / 2} y={-r} width={pillWidth} height={size} rx={r} transform={tf} />
			);
		case 'rectangle':
			return (
				<rect
					x={-rectangleWidth / 2}
					y={-rectangleHeight / 2}
					width={rectangleWidth}
					height={rectangleHeight}
					rx={rectangleBorderRadius ?? Math.min(rectangleWidth, rectangleHeight) / 4}
					transform={tf}
				/>
			);
		case 'hexagon':
			return <polygon points={getHexagonPoints(0, 0, r)} transform={tf} />;
		case 'triangle':
			return <polygon points={getTrianglePoints(0, 0, r)} transform={tf} />;
		default:
			return <circle cx={cx} cy={cy} r={r} />;
	}
}

function renderFilledShape(
	L: NodeOverlapLayout,
	fill: FillColor,
	fillOpacity?: number,
): React.ReactNode {
	const { r, size, pillWidth, rectangleWidth, rectangleHeight, rectangleBorderRadius, shape } = L;
	const op = fillOpacity !== undefined ? { fillOpacity } : {};
	const cssFill = getFillColor(fill);
	switch (shape) {
		case 'circle':
			return <circle cx={0} cy={0} r={r} fill={cssFill} {...op} />;
		case 'pill':
			return (
				<rect
					x={-pillWidth / 2}
					y={-r}
					width={pillWidth}
					height={size}
					rx={r}
					fill={cssFill}
					{...op}
				/>
			);
		case 'rectangle':
			return (
				<rect
					x={-rectangleWidth / 2}
					y={-rectangleHeight / 2}
					width={rectangleWidth}
					height={rectangleHeight}
					rx={rectangleBorderRadius ?? Math.min(rectangleWidth, rectangleHeight) / 4}
					fill={cssFill}
					{...op}
				/>
			);
		case 'hexagon':
			return <polygon points={getHexagonPoints(0, 0, r)} fill={cssFill} {...op} />;
		case 'triangle':
			return <polygon points={getTrianglePoints(0, 0, r)} fill={cssFill} {...op} />;
		default:
			return <circle cx={0} cy={0} r={r} fill={cssFill} {...op} />;
	}
}

function resolveIntersectionImageAabb(Ls: NodeOverlapLayout, Lt: NodeOverlapLayout): WorldAabb {
	const tight = getShapeIntersectionAabb(Ls, Lt);
	if (tight) return tight;

	const loose = intersectWorldAabbs(layoutWorldAabb(Ls), layoutWorldAabb(Lt));
	if (loose) {
		const cx = (loose.minX + loose.maxX) / 2;
		const cy = (loose.minY + loose.maxY) / 2;
		const side = Math.max(Math.min(loose.maxX - loose.minX, loose.maxY - loose.minY, 48), 6);
		return {
			minX: cx - side / 2,
			maxX: cx + side / 2,
			minY: cy - side / 2,
			maxY: cy + side / 2,
		};
	}

	const cx = (Ls.cx + Lt.cx) / 2;
	const cy = (Ls.cy + Lt.cy) / 2;
	const s = Math.min(Ls.r, Lt.r, 24) * 0.5;
	return { minX: cx - s, maxX: cx + s, minY: cy - s, maxY: cy + s };
}

function renderOverlapImageInIntersection(href: string, aabb: WorldAabb): React.ReactNode {
	const w = Math.max(aabb.maxX - aabb.minX, 1);
	const h = Math.max(aabb.maxY - aabb.minY, 1);
	return (
		<image
			href={href}
			x={aabb.minX}
			y={aabb.minY}
			width={w}
			height={h}
			preserveAspectRatio="xMidYMid slice"
		/>
	);
}

function normalizeOverlapFill(fill: OverlapFill): {
	color?: FillColor;
	backgroundImage?: string;
} {
	if (typeof fill === 'string') {
		return { color: fill };
	}
	return {
		color: fill.color,
		backgroundImage: fill.backgroundImage,
	};
}

type ParsedOverlapLayer = {
	sourceId: string;
	targetId: string;
	parsed: ReturnType<typeof normalizeOverlapFill>;
	imageAabb?: WorldAabb;
};

const DEFAULT_OVERLAP_FADE_DURATION_SEC = 0.5;

type Props = {
	nodes: WebNodeConfig[];
	viewBoxHeight: number;
	animated?: boolean;
	delay?: number;
	loadEntranceFadeDurationSec?: number;
	skipLoadEntrance?: boolean;
	nodePositions?: ReadonlyMap<string, NodeCenterPct>;
};

export const WebOverlapLayers = ({
	nodes,
	viewBoxHeight,
	animated = false,
	delay = 0,
	loadEntranceFadeDurationSec = DEFAULT_OVERLAP_FADE_DURATION_SEC,
	skipLoadEntrance = false,
	nodePositions,
}: Props) => {
	const reactId = useId().replace(/:/g, '');
	const nodeMap = useMemo(() => new Map(nodes.map((n) => [n.id, n] as const)), [nodes]);

	const { clipIds, layers } = useMemo(() => {
		const clipIds = new Set<string>();
		const layers: ParsedOverlapLayer[] = [];

		for (const node of nodes) {
			if (!node.overlapColors?.length) continue;
			for (const entry of node.overlapColors) {
				const otherId = entry.elementIdToOverlap;
				const otherNode = nodeMap.get(otherId);
				if (!otherNode) continue;
				const parsed = normalizeOverlapFill({
					color: entry.color,
					backgroundImage: entry.backgroundImage,
				});
				if (!parsed.color && !parsed.backgroundImage) continue;

				clipIds.add(otherId);
				const Ls = getNodeOverlapLayout(node, viewBoxHeight, nodePositions?.get(node.id));
				const Lt = getNodeOverlapLayout(otherNode, viewBoxHeight, nodePositions?.get(otherId));

				let imageAabb: WorldAabb | undefined;
				if (parsed.backgroundImage) {
					clipIds.add(node.id);
					imageAabb = resolveIntersectionImageAabb(Ls, Lt);
				}

				layers.push({
					sourceId: node.id,
					targetId: otherId,
					parsed,
					imageAabb,
				});
			}
		}
		return { clipIds, layers };
	}, [nodes, nodeMap, viewBoxHeight, nodePositions]);

	const clipId = (nodeId: string) => `overlap-clip-${reactId}-${nodeId}`;

	if (layers.length === 0) return null;

	return (
		<>
			<defs>
				{nodes
					.filter((n) => clipIds.has(n.id))
					.map((n) => {
						const L = getNodeOverlapLayout(n, viewBoxHeight, nodePositions?.get(n.id));
						return (
							<clipPath key={clipId(n.id)} id={clipId(n.id)} clipPathUnits="userSpaceOnUse">
								{clipPathShape(L)}
							</clipPath>
						);
					})}
			</defs>
			<g
				style={{
					isolation: 'isolate',
					pointerEvents: 'none',
					opacity: animated ? 1 : 0,
					...(animated && skipLoadEntrance
						? {}
						: {
								transition: `opacity ${loadEntranceFadeDurationSec}s ease ${delay}s`,
							}),
				}}
			>
				{layers.map(({ sourceId, targetId, parsed, imageAabb }) => {
					const source = nodeMap.get(sourceId);
					if (!source) return null;
					const L = getNodeOverlapLayout(source, viewBoxHeight, nodePositions?.get(sourceId));
					const hasImage = Boolean(parsed.backgroundImage);
					const hasColor = Boolean(parsed.color);

					if (hasImage && imageAabb) {
						return (
							<g key={`${sourceId}-${targetId}`} style={{ mixBlendMode: 'normal' as const }}>
								<g clipPath={`url(#${clipId(targetId)})`}>
									<g clipPath={`url(#${clipId(sourceId)})`}>
										{hasColor && parsed.color ? (
											<g transform={`translate(${L.cx},${L.cy}) rotate(${L.rotate})`}>
												{renderFilledShape(L, parsed.color)}
											</g>
										) : null}
										{renderOverlapImageInIntersection(parsed.backgroundImage ?? '', imageAabb)}
									</g>
								</g>
							</g>
						);
					}

					const fillContent =
						parsed.color !== undefined ? renderFilledShape(L, parsed.color) : null;

					return (
						<g key={`${sourceId}-${targetId}`} style={{ mixBlendMode: 'normal' as const }}>
							<g clipPath={`url(#${clipId(targetId)})`}>
								<g transform={`translate(${L.cx},${L.cy}) rotate(${L.rotate})`}>{fillContent}</g>
							</g>
						</g>
					);
				})}
			</g>
		</>
	);
};
